import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  Autocomplete,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import { useDropzone, DropzoneOptions } from 'react-dropzone';
import { useChat } from '../../context/chatContext'; // Assuming context provides userId
import { 
  listPersonalities, 
  getPersonalityFile, 
  updatePersonalityFile, 
  uploadPersonalityFile,
  deletePersonalityFile
} from '../../services/api'; // Assuming API functions are in this path

// --- TYPE DEFINITIONS ---
interface Personality {
  name: string;
}

interface PersonalityWithContent extends Personality {
  content: string;
}

// --- MAIN COMPONENT ---
const ManagePersonalities: React.FC = () => {
  // --- STATE MANAGEMENT ---
  const { userId, setSessionId, setToken } = useChat();
  const [personalities, setPersonalities] = useState<Personality[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [editingPersonality, setEditingPersonality] = useState<PersonalityWithContent | null>(null);
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const [newPersonalityName, setNewPersonalityName] = useState('');

  // --- DATA FETCHING ---
  const fetchPersonalities = useCallback(async () => {
    if (!userId) {
        setIsLoading(false);
        return;
    };
    setIsLoading(true);
    console.log('setting personalities', isLoading)
    setError(null);
    try {
      // Fetch personal personalities (org flag is false)
      const data = await listPersonalities(userId, setSessionId, setToken);
      setPersonalities(data.files.map((name: string) => ({ name })));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred while fetching personalities.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [userId, setSessionId, setToken]);

  useEffect(() => {
    fetchPersonalities();
  }, [fetchPersonalities]);

  // --- DROPZONE SETUP ---
  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFilesToUpload(prevFiles => {
        const newFiles = acceptedFiles.filter(
            newFile => !prevFiles.some(prevFile => prevFile.name === newFile.name && prevFile.size === newFile.size)
        );
        return [...prevFiles, ...newFiles];
    });
  }, []);

  const dropzoneOptions: DropzoneOptions = {
    onDrop,
    accept: { 
        'text/plain': ['.txt'],
        'application/pdf': ['.pdf'],
        'application/zip': ['.zip'],
    },
    multiple: true,
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone(dropzoneOptions);
  
  const handleRemoveFile = (fileName: string) => {
    setFilesToUpload(prevFiles => prevFiles.filter(file => file.name !== fileName));
  };

  // --- EVENT HANDLERS ---
  const handleUploadClick = async () => {
    if (filesToUpload.length === 0 || !newPersonalityName || !userId) {
        setError('Please provide a name and select at least one file to upload.');
        return;
    }

    setIsUploading(true);
    setError(null);

    try {
      // Upload as a personal personality (org flag is false)
      await uploadPersonalityFile(newPersonalityName, filesToUpload, userId, false, setSessionId, setToken);
      setFilesToUpload([]);
      setNewPersonalityName('');
      await fetchPersonalities(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during upload.');
      console.error(err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleEditClick = async (personality: Personality) => {
    if (!userId) return;
    setError(null);
    try {
        // Get personal personality file (org flag is false)
        const data = await getPersonalityFile(userId, personality.name, setSessionId, setToken);
        setEditingPersonality({ name: personality.name, content: data.content });
    } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load personality content.');
        console.error(err);
    }
  };
  
  const handleDeleteClick = async (personalityName: string) => {
    if (!userId || !window.confirm(`Are you sure you want to delete "${personalityName}"? This action cannot be undone.`)) {
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      // Delete personal personality file (org flag is false)
      await deletePersonalityFile(userId, personalityName, setSessionId, setToken);
      await fetchPersonalities(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete personality.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!editingPersonality || !userId) return;
    
    try {
        // Update personal personality file (org flag is false)
        await updatePersonalityFile(userId, editingPersonality.name, editingPersonality.content, setSessionId, setToken);
        setEditingPersonality(null);
        await fetchPersonalities();
    } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save changes.');
        console.error(err);
    }
  };
  
  const handleCloseDialog = () => {
    setEditingPersonality(null);
    setError(null);
  };

  // --- RENDER ---
  return (
    <Box sx={{ p: 3 }}>

      {error && !editingPersonality && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* --- UPLOAD SECTION --- */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Upload New Personality
        </Typography>

        <Autocomplete
          freeSolo
          options={personalities.map((p) => p.name)}
          value={newPersonalityName}
          onInputChange={(_, newValue) => setNewPersonalityName(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="New Personality Name"
              variant="outlined"
              margin="normal"
            />
          )}
        />

        <Box
          {...getRootProps()}
          sx={{
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'divider',
            borderRadius: 1,
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
            transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
            mb: 2,
          }}
        >
          <input {...getInputProps()} />
          <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography>Drag & drop files here, or click to select</Typography>
          <Typography variant="body2" color="text.secondary">.txt, .pdf, and .zip files are supported</Typography>
        </Box>
        
        {filesToUpload.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2">Files to upload:</Typography>
            <List dense>
              {filesToUpload.map((file) => (
                <ListItem
                  key={`${file.name}-${file.lastModified}`}
                  secondaryAction={
                    <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveFile(file.name)}>
                      <CloseIcon />
                    </IconButton>
                  }
                >
                  <ListItemText primary={file.name} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        <Button
          variant="contained"
          onClick={handleUploadClick}
          disabled={isUploading || filesToUpload.length === 0 || !newPersonalityName}
          fullWidth
        >
          {isUploading ? 'Uploading...' : `Upload Personality`}
        </Button>
        {isUploading && <LinearProgress sx={{ mt: 1 }} />}
      </Paper>

      {/* --- PERSONALITIES TABLE --- */}
      <Typography variant="h6" gutterBottom sx={{mt: 3}}>
        Existing Personalities
      </Typography>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="personalities table">
          <TableHead>
            <TableRow>
              <TableCell>Personality Name</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                  <Typography>Loading Personalities...</Typography>
                </TableCell>
              </TableRow>
            ) : personalities.length === 0 ? (
                <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                        <Typography>No personal personalities found.</Typography>
                    </TableCell>
                </TableRow>
            ) : (
              personalities.map((p) => (
                <TableRow key={p.name} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row">
                    {p.name}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton onClick={() => handleEditClick(p)} aria-label="edit">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton onClick={() => handleDeleteClick(p.name)} aria-label="delete">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* --- EDIT DIALOG --- */}
      {editingPersonality && (
        <Dialog 
          open={!!editingPersonality} 
          onClose={handleCloseDialog} 
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Edit Personality: {editingPersonality.name}</DialogTitle>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
              autoFocus
              margin="dense"
              id="content"
              label="Personality Content"
              type="text"
              fullWidth
              multiline
              rows={15}
              variant="outlined"
              value={editingPersonality.content}
              onChange={(e) => setEditingPersonality({ ...editingPersonality, content: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSaveChanges} variant="contained">Save Changes</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ManagePersonalities;
